import { useContext } from "react";

import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { css } from "@emotion/react";
import Link from "@mui/material/Link";

import { colors } from "@dotnet/telematics-design-system";

import ParamsContext from "context/params/paramsContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { LinkLoader } from "shared/loaders/LinkLoader";
import { body4BoldStyles } from "shared/styles/typography";
import { FILTER_NAMES, NOT_FILTER_NAMES } from "shared/types/enums";

import { CompanyLinkProps } from "../types/interfaces";

const CompanyLink = ({
    data,
    counter,
    isInitialLoad,
    isLoading,
    companyId,
    companyName
}: CompanyLinkProps) => {
    const { gray100, gray200, gray700, gray900 } = colors;

    const { queryParams, updateFilterParams } = useContext(ParamsContext);

    const { t } = useTranslation();

    const { renderFormattedNumber } = useFormattedNumber();

    const { id: linkId, path, icon: linkIcon } = data;

    const notEmpty = counter[linkId] > 0;
    const titleName = linkId;

    const component = () => {
        if (!notEmpty) {
            return "div";
        }

        return RouterLink;
    };

    const Icon = linkIcon;

    const filterChildCompanies = () => {
        if (notEmpty) {
            const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];

            const getFilteredValues = () => ({
                filterName: FILTER_NAMES.CompanyId,
                value: [{ id: companyId, name: companyName }]
            });

            updateFilterParams(
                {
                    [FILTER_NAMES.RootCompany]: rootCompanyId,
                    [FILTER_NAMES.CompanyId]: String(companyId),
                    [NOT_FILTER_NAMES.Selected]: ""
                },
                getFilteredValues()
            );
        }
    };

    return (
        <Link
            component={component()}
            to={`${path}?${FILTER_NAMES.CompanyId}=${companyId}`}
            onClick={filterChildCompanies}
            underline="none"
            css={css({
                opacity: !isInitialLoad && isLoading ? "0.4" : "1",
                cursor: `${notEmpty ? "pointer" : "auto"}`,
                display: "flex",
                columnGap: "8px",
                alignItems: "center",
                borderRadius: "10px",
                padding: "4px 8px 4px 4px",
                minWidth: "156px",
                boxSizing: "border-box",
                transition:
                    "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

                "&:hover": {
                    background: notEmpty ? `${gray100}` : "unset"
                }
            })}
        >
            {isInitialLoad ? (
                <LinkLoader />
            ) : (
                <>
                    <div
                        css={css({
                            borderRadius: "8px",
                            padding: "7px",
                            border: `1px solid ${gray200}`,
                            color: gray700,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",

                            "& > svg": {
                                fontSize: "24px"
                            }
                        })}
                    >
                        <Icon />
                    </div>

                    <div
                        css={css({
                            display: "flex",
                            flexDirection: "column",

                            "& > span:first-of-type": {
                                color: gray900,
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "20px",
                                letterSpacing: "0.1px"
                            },

                            "& > span:last-of-type": {
                                ...body4BoldStyles,
                                color: gray700,
                                fontWeight: 700
                            }
                        })}
                    >
                        <span>{t(`Table##${titleName}`)}</span>

                        <span>
                            {renderFormattedNumber(counter[linkId])}{" "}
                            {t(`Table##items`)}
                        </span>
                    </div>
                </>
            )}
        </Link>
    );
};

export default CompanyLink;
